import React, { useEffect, useRef } from "react";
import { GamesSearchInput } from "@/v2-components/pages/games/search/GamesSearchInput";
import GamesTabMenu from "@/v2-components/pages/games/GameTabMenu";
import GlobalNavigationBar from "@/v2-components/global-navigation-bar";
import type { MenuName } from "@/v2-components/global-navigation-bar/_const/navigation-menus";
import PromoBanner from "@/components/banner/PromoBanner";
import { cn } from "@/libs/utils";
import { usePathname } from "next/navigation";
import { useIsMobileView } from "@/contexts/ResponsiveProvider";
import { useRouter } from "next/router";
import CarotLeftIcon from "@/v2-components/core/icon/carot-left-icon";
import PageTemplate from "@/components/templates/PageTemplate";
import ReferralBanner from "@/components/banner/ReferralBanner";
import { useResizeObserver } from "@/hooks/useResizeObserver";
import { useHiddenOnScroll } from "@/hooks/useHiddenOnScroll";
import { useLocale } from "@/store/locale-store";
import SuggestLocale from "@/components/SuggestLocale";

type Props = {
  countryCode: string;
  children?: React.ReactNode;
  /**
   * 상단 2depth 내비. 넘기면 **옛 게임 탭바(`GamesTabWrap`)를 대체**한다.
   *
   * 개정 시안(`29889:101361` 데스크탑 · `30035:18958` 모바일)에서 상세 상단은
   * `Overview · VR Games · Software` 3탭이다. 종전 탭바는 **검색창 + 9개 탭**
   * (Home · AI Powered · PC Game · Sim Racing · Meta Quest · Steam VR · Pico ·
   * PSVR2 · Audio-to-Haptics)이라 시안과 다르다.
   *
   * ⚠️ `GamesPageTemplate` 과 **같은 프롭 이름**을 쓴다 — 두 템플릿이 갈리면 한쪽만
   * 고치게 된다(그 파일 주석의 경고와 같은 이유).
   */
  tabBar?: React.ReactNode;
  /**
   * GNB 1차 메뉴 활성 override.
   *
   * ★상세 경로(`/games/experiences/{slug}`)는 **PC·VR 공용**이라 경로로 못 가른다 —
   * `VR Games` 의 `href` 가 `/games` 라 `/games/*` 를 통째로 잡아, PC 게임 상세에서도
   * VR 이 볼드였다(2026-09-02 실측). 서브내비는 이미 `section` 을 받아 맞게 그렸고
   * **GNB 만 그 값을 안 받고 있었다.** 사유·수치는 `getActiveMenuName` 주석.
   *
   * ⚠️ 이 파일은 GNB 를 **두 곳**(모바일·데스크탑)에서 렌더한다 — 한쪽만 넘기면
   * 폭에 따라 갈린다. `__tests__` 가 두 자리를 센다.
   */
  activeMenuOverride?: MenuName;
  onHeaderChange?: ({ height }: { height: number }) => void;
};

const GameDetailPageTemplate = ({
  children,
  countryCode,
  onHeaderChange,
  tabBar,
  activeMenuOverride,
}: Props) => {
  const isMobileView = useIsMobileView();

  return (
    <>
      {isMobileView ? (
        <Mobile
          countryCode={countryCode}
          onHeaderChange={onHeaderChange}
          tabBar={tabBar}
          activeMenuOverride={activeMenuOverride}
        >
          {children}
        </Mobile>
      ) : (
        // Desktop 은 스티키 띠가 헤더 높이를 직접 쓰므로 onHeaderChange 가 필요 없다
        <Desktop
          countryCode={countryCode}
          tabBar={tabBar}
          activeMenuOverride={activeMenuOverride}
        >
          {children}
        </Desktop>
      )}
    </>
  );
};

const Mobile = ({
  children,
  onHeaderChange,
  countryCode,
  tabBar,
  activeMenuOverride,
}: Props) => {
  const headerRef = useRef<HTMLDivElement>(null);
  const { height: headerHeight } = useResizeObserver(headerRef);
  const prevHeaderHeight = useRef<number>(headerHeight);
  const locale = useLocale();
  const canSuggeset = !!countryCode && locale.countryCode !== countryCode;

  // 모바일은 스티키 띠가 없어서 헤더 높이를 페이지에 알려주는 역할만 한다
  // (목차 sticky top 계산용 — pages/games/experiences/[slug]/index.tsx).
  useEffect(() => {
    if (prevHeaderHeight.current !== headerHeight) {
      prevHeaderHeight.current = headerHeight;
      onHeaderChange?.({ height: headerHeight });
    }
  }, [onHeaderChange, headerHeight]);

  return (
    <PageTemplate>
      <PageTemplate.Content useCustomNav={true}>
        <div ref={headerRef} className="sticky top-0 z-30">
          {canSuggeset && <SuggestLocale countryCode={countryCode} />}
          <ReferralBanner />
          <GlobalNavigationBar activeMenuOverride={activeMenuOverride} />
          <PromoBanner />
        </div>
        {tabBar}
        {children}
      </PageTemplate.Content>
    </PageTemplate>
  );
};

const Desktop = ({
  children,
  countryCode,
  tabBar,
  activeMenuOverride,
}: Props) => {
  const headerRef = useRef<HTMLDivElement>(null);
  const { height: headerHeight } = useResizeObserver(headerRef);
  const { targetRef, hidden, containerProps } =
    useHiddenOnScroll<HTMLDivElement>({
      throttleMs: 100,
      enabled: true,
      threshold: 80,
    });
  const locale = useLocale();
  const canSuggeset = !!countryCode && locale.countryCode !== countryCode;
  const stickyTop = hidden ? 0 : headerHeight;

  return (
    <PageTemplate>
      <PageTemplate.Content useCustomNav={true}>
        {/* GamesPageTemplate 과 동일 구조 (한쪽만 바꾸면 어긋난다).
            containerProps · z 스케일 · SuggestLocale 배치 근거는 그쪽 주석과
            docs/pitfalls.md #36(스크롤로 숨는 스티키 헤더) 참조. */}
        <div
          {...containerProps}
          ref={headerRef}
          data-testid="games-sticky-header"
          className="sticky top-0 z-30"
        >
          <div
            ref={targetRef}
            data-testid="games-header-animated"
            className="transition-[transform,opacity] duration-200"
          >
            {canSuggeset && <SuggestLocale countryCode={countryCode} />}
            <ReferralBanner />
            <GlobalNavigationBar activeMenuOverride={activeMenuOverride} />
            <PromoBanner />
          </div>
        </div>
        <div
          data-testid="games-sticky-tabbar"
          /* ★**이 파일에서 `pt-4` 는 사구간이었다** — 형제 `GamesPageTemplate` 과 달리
             라이브 결함이 아니었다(#5598 R1 M1 정정).

             이 템플릿은 폭으로 분기하는데 두 구간이 정확히 맞물린다:

               useIsMobileView = max-width: (BREAKPOINTS.laptop - 1) = 1023px
               laptop:         = 1024px

               ≤1023 → <Mobile>    스티키 래퍼가 **없다** — `{tabBar}` 를 맨몸으로 렌더
               ≥1024 → <Desktop>   이 래퍼 · 거기선 `laptop:pt-0` 이 항상 이긴다

             ⇒ `pt-4` 가 살아 있는 폭이 **0** 이다. 이 파일의 전·후는 둘 다 0 이다.
             모바일에 16px 띠가 생긴 것은 **분기 없이 단일 렌더**인 `GamesPageTemplate`
             하나였다 — 기전·시안 근거는 그쪽 주석이 원문이다.

             그래도 함께 만지는 이유는 **복제 템플릿이 갈리지 않게** 하는 것이다.
             두 파일이 같은 스티키 래퍼 문자열과 각자의 `GamesTabWrap` 을 갖고 있고,
             `games-subnav-top-gap.test.ts` 의 「두 템플릿이 갈리지 않는다」 케이스가
             그 동일성을 잠근다. 나중에 `<Mobile>` 이 스티키 띠를 갖거나 폴백에 소비처가
             생기면 그때 이 값이 실제로 걸린다.

             ⚠️ 아래 폴백(`GamesTabWrap`)도 **도달 0곳**이다 — 이 템플릿의 소비처는
             `pages/games/experiences/[slug]/index.tsx` 하나이고 `tabBar` 를 **항상**
             넘긴다(:158). 게다가 그 폴백은 `<Desktop>` 안이라 ≥1024 에서만이다. */
          className="sticky z-20 bg-white"
          style={{ top: `${stickyTop}px` }}
        >
          {tabBar ?? <GamesTabWrap />}
          <hr className="absolute bottom-0 hidden h-px w-full laptop:flex" />
        </div>
        {children}
      </PageTemplate.Content>
    </PageTemplate>
  );
};

export default GameDetailPageTemplate;

const GamesTabWrap = () => {
  const router = useRouter();
  const pathname = usePathname();
  const isMobileView = useIsMobileView();
  const isSearchPage = pathname.includes("search");
  const shouldShowTabMenu = !isSearchPage || (isSearchPage && !isMobileView);
  return (
    // `pt-4 laptop:pt-0` 는 종전에 스티키 래퍼가 갖고 있던 값이다 — 이 옛 배치의 여백이라
    // 여기로 옮겼다(#5568). 래퍼에 두면 `tabBar` 를 넘기는 리뉴얼 페이지에도 붙는다.
    <div className="mx-auto flex h-full w-full max-w-screen-max flex-col gap-3 pt-4 laptop:flex-row-reverse laptop:items-center laptop:justify-between laptop:gap-0 laptop:pt-0">
      <div
        className={cn(
          "mx-4 flex items-center gap-3 laptop:ml-0 max:mx-0",
          !shouldShowTabMenu && "pb-4",
        )}
      >
        {!shouldShowTabMenu && (
          <button onClick={() => router.back()}>
            <CarotLeftIcon />
          </button>
        )}

        <GamesSearchInput />
      </div>
      {shouldShowTabMenu && (
        <div className="flex flex-col gap-3 laptop:gap-0">
          <GamesTabMenu />
        </div>
      )}
    </div>
  );
};
